<template>
        <button
        class="option-button"
        :class="{
            'option-button--active': active || loading,
            'option-button--inactive': !active && !loading
        }"
        :disabled="disabled || loading"
        @click="$emit('click', $event)"
    >
        <div class="option-button__icon-container">
            <div
                class="option-button__icon"
                :class="{
                    'option-button__icon--active': active && !loading,
                    'option-button__icon--inactive': !active || loading,
                    'option-button__icon--loading': loading
                }"
            >
                <i
                    v-if="loading"
                    class="fas fa-spinner fa-spin option-button__icon-element option-button__loader"
                ></i>
                <i
                    v-else-if="active && activeIcon"
                    :class="activeIcon"
                    class="option-button__icon-element"
                ></i>
                <i
                    v-else-if="!active && inactiveIcon"
                    :class="inactiveIcon"
                    class="option-button__icon-element"
                ></i>
            </div>
        </div>
        
        <div class="option-button__content">
            <div class="option-button__text-container">
                <span class="option-button__title">
                    {{ title }}
                </span>
                <span
                    v-if="subtitle || loading"
                    class="option-button__subtitle"
                >
                    {{ loading ? loadingText : subtitle }}
                </span>
            </div>
            <span
                v-if="active && badgeText"
                class="option-button__badge"
            >
                {{ badgeText }}
            </span>
        </div>
        
        <i
            v-if="showArrow && !loading"
            class="fas fa-arrow-right option-button__arrow"
        ></i>
    </button>
</template>

<script>
export default {
    name: 'OptionButton',
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            required: true,
        },
        subtitle: {
            type: String,
            default: null,
        },
        activeIcon: {
            type: String,
            default: 'fas fa-check',
        },
        inactiveIcon: {
            type: String,
            default: null,
        },
        badgeText: {
            type: String,
            default: null,
        },
        showArrow: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        loadingText: {
            type: String,
            default: 'Loading...',
        },
    },
    emits: ['click'],
};
</script>

<style lang="scss" scoped>
.option-button {
    align-items: center;
    background-color: $white;
    border: 1px solid $accounts-secondary-100;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    gap: 12px;
    padding: 16px;
    transition: all 0.2s ease-in-out;
    width: 100%;
    text-align: left;

    &:hover:not(:disabled) {
        border-color: var(--color-primary-600);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    &--active {
        background-color: var(--color-button-secondary-background-secondary);
        border-color: var(--color-primary-600);
    }

    &--inactive {
        background-color: $white;
        border-color: var(--color-primary-300);

        &:hover:not(:disabled) {
            background-color: var(--color-button-secondary-background-secondary);
        }
    }

    &__icon-container {
        flex-shrink: 0;
    }

    &__icon {
        align-items: center;
        border-radius: 50%;
        display: flex;
        height: 40px;
        justify-content: center;
        width: 40px;

        &--active {
            background-color: var(--color-primary-600);
        }

        &--inactive {
            background-color: #E8E8F5;
        }

        &--loading {
            background-color: #E8E8F5;
        }
    }

    &__icon-element {
        font-size: 18px;

        .option-button__icon--active & {
            color: $white;
        }

        .option-button__icon--inactive & {
            color: $accounts-secondary-600;
        }
    }

    &__loader {
        color: $accounts-secondary-600;
        
        .option-button__icon--inactive & {
            color: $accounts-secondary-600;
        }
    }

    &__content {
        align-items: center;
        display: flex;
        flex: 1;
        gap: 12px;
        justify-content: space-between;
    }

    &__text-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__title {
        color: #333333;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.4;
    }

    &__subtitle {
        color: #787F84;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
    }

    &__badge {
        background-color: $accounts-primary-600;
        border-radius: 12px;
        color: $white;
        font-size: 12px;
        font-weight: 500;
        padding: 4px 12px;
        white-space: nowrap;
    }

    &__arrow {
        color: #333333;
        flex-shrink: 0;
        font-size: 14px;
    }
}
</style>

